import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Box,
  HStack,
  Image,
  RippleButton,
  Spinner,
  Text,
  VStack,
} from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import type { SubjectiveQuestion } from "../../types/subjectiveQuestion";
import styles from "./SubjectiveQuestionView.module.scss";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export interface SubjectiveQuestionViewProps {
  questions: SubjectiveQuestion[];
  activeQuestionId: string;
  onSelectQuestion: (questionId: string) => void;
  onViewPdf?: (pdfUrl?: string) => void;
  assetRenderer?: ReactNode;
  topContent?: ReactNode;
}

export const SubjectiveQuestionView = ({
  questions,
  activeQuestionId,
  onViewPdf,
  assetRenderer,
}: SubjectiveQuestionViewProps) => {
  const { t } = useTranslation("tests");
  const { theme } = useTheme();

  const active = useMemo(
    () => questions.find((q) => q.questionId === activeQuestionId),
    [questions, activeQuestionId],
  );

  const pdfFrameRef = useRef<HTMLDivElement | null>(null);
  const scrollContentRef = useRef<HTMLDivElement | null>(null);
  const [pdfFrameWidth, setPdfFrameWidth] = useState(0);
  const [pdfPageCount, setPdfPageCount] = useState(0);

  useEffect(() => {
    scrollContentRef.current?.scrollTo({ top: 0 });
  }, [activeQuestionId]);

  useEffect(() => {
    const node = pdfFrameRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      setPdfFrameWidth(width);
    });
    observer.observe(node);
    setPdfFrameWidth(node.clientWidth);
    return () => observer.disconnect();
  }, [active?.pdfUrl]);

  useEffect(() => {
    setPdfPageCount(0);
  }, [active?.pdfUrl]);

  return (
    <VStack className={styles.container} gap="md">
      {active && (
        <Box className={styles.scrollContent} ref={scrollContentRef}>
          <VStack gap="md" className={styles.scrollInner}>
            <HStack
              className={styles.scoreBar}
              justify="space-between"
              align="center"
            >
              <Text variant="body" size="medium" color={theme.colors.text[10]}>
                {t("score")}
              </Text>
              <Text
                variant="body"
                size="large"
                weight="emphasized"
                color={theme.colors.text[10]}
              >
                {active.scoreObtained}/{active.scoreMax}
              </Text>
            </HStack>

            {assetRenderer && (
              <Box className={styles.assetCard}>{assetRenderer}</Box>
            )}

            {(active.pdfUrl || active.answerSheetImageUrl) && (
              <Box className={styles.answerCard}>
                <span className={styles.answerCardTitle}>
                  {t("students_answer_sheet")}
                </span>
                {active.pdfUrl && !active.answerSheetImageUrl ? (
                  <Box className={styles.imageFrame} ref={pdfFrameRef}>
                    <Document
                      key={active.pdfUrl}
                      file={active.pdfUrl}
                      loading={
                        <Box className={styles.pdfLoading}>
                          <Spinner size="lg" />
                        </Box>
                      }
                      error={null}
                      noData={null}
                      onLoadSuccess={({ numPages }: { numPages: number }) =>
                        setPdfPageCount(numPages)
                      }
                      className={styles.pdfDocument}
                    >
                      {pdfFrameWidth > 0 &&
                        Array.from({ length: pdfPageCount }, (_, i) => (
                          <div
                            key={`pdf-page-${i + 1}`}
                            className={styles.pdfPageWrap}
                          >
                            <Page
                              pageNumber={i + 1}
                              width={pdfFrameWidth}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                          </div>
                        ))}
                    </Document>
                  </Box>
                ) : (
                  <Box className={styles.imageFrame}>
                    <Image
                      source={active.answerSheetImageUrl}
                      resizeMode="contain"
                      accessibilityLabel={t("question_paper_and_solutions")}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </Box>
                )}
                {active.pdfUrl && (
                  <HStack justify="center">
                    <RippleButton
                      variant="text"
                      size="md"
                      shape="round"
                      onPress={() => onViewPdf?.(active.pdfUrl)}
                    >
                      {t("view_pdf_answer_sheet")}
                    </RippleButton>
                  </HStack>
                )}
              </Box>
            )}

            {active.revisedScoreReason && (
              <VStack className={styles.revisedReasonCard} gap="sm">
                <Text
                  variant="label"
                  size="large"
                  weight="emphasized"
                  color={theme.colors.neutral[40]}
                >
                  {active.challengeStatus === "evaluated"
                    ? t("reason_for_revised_score")
                    : t("reason_for_score")}
                </Text>
                <Text
                  variant="body"
                  size="medium"
                  color={theme.colors.neutral[40]}
                  style={{ whiteSpace: "pre-line" }}
                >
                  {active.revisedScoreReason}
                </Text>
              </VStack>
            )}

            <Box className={styles.bottomSpacer} aria-hidden />
          </VStack>
        </Box>
      )}
    </VStack>
  );
};
